import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `valueProps` block.
 *
 * Field tree mirrors the props/data the React component (`./value-props.tsx`)
 * needs to render Reklaim's three-pillar "Why Join" section as decomposed
 * from `src/components/home/ValuePropSection.tsx`.
 *
 * This is NOT a byte-for-byte match with
 * `reklaim-services/apps/marketing/payload/blocks/value-props.ts` -- the
 * target Payload DB at cutover is fresh (per plan Q9), so where Reklaim's
 * component shape diverges from the existing marketing block, Reklaim's
 * shape wins. The marketing block is reference, not destination.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `value-props.tsx` / `value-props.payload.ts`
 *   React component export:  `ValueProps`
 *   Payload `slug`:          `valueProps`
 *   Payload `interfaceName`: `ValuePropsBlock`
 */

export const ValuePropsBlock: BlockConfig = {
  slug: 'valueProps',
  interfaceName: 'ValuePropsBlock',
  labels: {
    singular: 'Value Props',
    plural: 'Value Props Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge',
      admin: {
        description: 'Small pill label above the headline. Example: "Why Join".',
      },
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: {
        rows: 2,
        description:
          'Main section headline. The "headlineEmphasis" text is appended in the primary accent color.',
      },
    },
    {
      name: 'headlineEmphasis',
      type: 'text',
      label: 'Headline emphasis',
      admin: {
        description:
          'Optional emphasized phrase rendered in the primary color directly after the headline.',
      },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: {
        rows: 2,
        description: 'Lead paragraph below the headline. Supports inline emphasis via {bold}text{/bold}.',
      },
    },
    {
      name: 'pillars',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 6,
      labels: { singular: 'Pillar', plural: 'Pillars' },
      label: 'Pillars',
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
            required: true,
          }),
          admin: {
            description:
              'PascalCase lucide-react icon name. Supported in `valueProps`: TrendingUp, Shield, Database, Heart, Star, Globe, Lock, Users, Wallet, Gift, Zap, Sparkles. Unknown names fall back to Sparkles.',
          },
        },
        {
          name: 'title',
          type: 'text',
          required: true,
          label: 'Title',
        },
        {
          name: 'description',
          type: 'textarea',
          required: true,
          label: 'Description',
          admin: { rows: 3 },
        },
        linkField({
          name: 'link',
          label: 'Link URL',
          required: true,
        }),
        {
          name: 'linkText',
          type: 'text',
          required: true,
          label: 'Link text',
        },
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
          admin: {
            description:
              'Tick if the URL points to a different domain. Opens in a new tab with rel="noopener noreferrer".',
          },
        },
      ],
    },
    {
      name: 'showCta',
      type: 'checkbox',
      defaultValue: true,
      label: 'Show bottom CTA',
    },
    {
      name: 'cta',
      type: 'group',
      label: 'Bottom CTA',
      admin: {
        condition: (_data, sibling) =>
          Boolean((sibling as { showCta?: boolean } | undefined)?.showCta),
      },
      fields: [
        {
          name: 'text',
          type: 'text',
          label: 'Button text',
          defaultValue: 'Get Started Free',
        },
        linkField({
          name: 'link',
          label: 'Button link',
          defaultValue: '/auth',
        }),
      ],
    },
  ],
};

export default ValuePropsBlock;
